/**
 * Insert Workflow Configuration Component
 *
 * Generic CSV upload component for data insertion workflows
 * Features:
 * - CSV file upload
 * - CSV parsing and validation
 * - Data preview
 * - Execute button enabled after successful CSV upload
 */

import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { WorkflowConfigProps } from '../../types/workflow-config';

export const InsertConfig: React.FC<WorkflowConfigProps> = ({
  onExecute,
  onClose,
  isLoading = false,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<Record<string, unknown>[] | null>(null);
  const [csvError, setCsvError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    // Validate CSV file
    if (!file.name.endsWith('.csv')) {
      alert('Please select a valid CSV file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Parse CSV file
    Papa.parse(file, {
      header: true, // First row is header
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          const errorMessages = results.errors.map(err => err.message).join(', ');
          setCsvError(`CSV parsing error: ${errorMessages}`);
          setCsvData(null);
          return;
        }

        // Check if we got data
        if (!results.data || results.data.length === 0) {
          setCsvError('CSV file is empty');
          setCsvData(null);
          return;
        }

        // Successfully parsed
        setCsvFile(file);
        setCsvData(results.data as Record<string, unknown>[]);
        setCsvError('');

        // Clear validation error when file is selected
        if (validationError) {
          setValidationError('');
        }
      },
      error: (error) => {
        setCsvError(`Failed to parse CSV: ${error.message}`);
        setCsvData(null);
      },
    });
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const validateInputs = (): { valid: boolean; error: string } => {
    // CSV file is uploaded and parsed successfully
    if (csvFile && csvData) {
      return { valid: true, error: '' };
    }

    // CSV file uploaded but parsing failed
    if (csvFile && !csvData) {
      return { valid: false, error: csvError || 'CSV parsing failed' };
    }

    // No CSV file uploaded
    return {
      valid: false,
      error: 'Bitte laden Sie eine CSV-Datei hoch.'
    };
  };

  const handleExecute = async () => {
    const validation = validateInputs();

    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    // Prepare data for workflow execution
    const executionData: Record<string, unknown> = {};

    if (csvFile && csvData) {
      // Send parsed CSV data as structured JSON
      executionData.rows = csvData;
      executionData.fileName = csvFile.name;
      executionData.rowCount = csvData.length;
      executionData.columns = csvData.length > 0 ? Object.keys(csvData[0]) : [];
    }

    await onExecute(executionData);
  };

  const validation = validateInputs();
  const canExecute = validation.valid;

  return (
    <div className="space-y-6">
      {/* CSV Upload Section */}
      <div>
        <Label className="mb-2">CSV-Datei hochladen</Label>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : csvFile
              ? 'border-primary/30 bg-primary/5'
              : 'border-border bg-background'
          }`}
        >
          <div className="space-y-1 text-center">
            {csvFile ? (
              <>
                <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
                <div className="flex text-sm text-muted-foreground">
                  <span className="font-medium text-primary">{csvFile.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {(csvFile.size / 1024).toFixed(2)} KB
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setCsvFile(null);
                    setCsvData(null);
                    setCsvError('');
                    if (validationError) {
                      setValidationError('');
                    }
                  }}
                  className="text-xs text-primary hover:text-primary/80"
                >
                  Datei entfernen
                </button>
              </>
            ) : (
              <>
                <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                <div className="flex text-sm text-muted-foreground">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md font-medium text-primary hover:text-primary/80"
                  >
                    <span>Datei hochladen</span>
                    <input
                      id="file-upload"
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="sr-only"
                      onChange={handleFileInputChange}
                    />
                  </label>
                  <p className="pl-1">oder per Drag & Drop</p>
                </div>
                <p className="text-xs text-muted-foreground">CSV-Dateien bis zu 10MB</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* CSV Preview Section */}
      {csvData && csvData.length > 0 && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-foreground">
                CSV erfolgreich geparst!
              </p>
              <div className="mt-2 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">{csvData.length}</strong> Zeilen gefunden mit <strong className="text-foreground">{Object.keys(csvData[0]).length}</strong> Spalten
                </p>
                <p className="mt-1">
                  Spalten: <span className="font-mono text-xs">{Object.keys(csvData[0]).join(', ')}</span>
                </p>
                {csvData.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-foreground">Vorschau (erste Zeile):</p>
                    <div className="mt-1 bg-background rounded-lg border border-border p-2 text-xs font-mono overflow-x-auto text-foreground">
                      {JSON.stringify(csvData[0], null, 2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Error Display */}
      {csvError && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-destructive">{csvError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Error */}
      {validationError && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-destructive">{validationError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-5 w-5 text-primary" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Hinweis:</strong> Laden Sie eine CSV-Datei hoch, um Daten in das System einzufügen.
              Die Daten werden automatisch geparst und strukturiert an den Workflow gesendet.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="ghost" onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button onClick={handleExecute} disabled={!canExecute || isLoading}>
          {isLoading ? 'Wird ausgeführt...' : 'Workflow ausführen'}
        </Button>
      </div>
    </div>
  );
};
